import { ApolloClient } from "@apollo/client"
import { useEffect } from "react"
import {
  OrderDocument,
  OrderFragment,
  OrderQuery,
  OrderQueryVariables,
  OrderStatus,
  OrderType,
  useOrderQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import {
  popSignupActivationContext,
  trackSignupActivateEvents
} from "../plausible/useSignupEventTracking"
import { ORDER_FULFILLED_EVENT } from "../utils/dispatcher"
import { dispatchOrderEvent } from "./utils/order-events"

export type OnOrderFulfilledCallback = (order: OrderFragment) => void

export type UseOnOrderFullfiledProps = {
  order?: OrderFragment | null
  onOrderFulfilled?: OnOrderFulfilledCallback
}

export const useOnOrderFullfiled = ({
  order,
  onOrderFulfilled
}: UseOnOrderFullfiledProps) => {
  const { me } = usePathwrightContext()
  const orderFullfilled =
    !!order && order.order_status === OrderStatus.Fulfilled

  useEffect(() => {
    if (orderFullfilled) {
      onOrderFulfilled && onOrderFulfilled(order)
      dispatchOrderEvent(ORDER_FULFILLED_EVENT, { user: me, order })
    }
  }, [orderFullfilled])
}

export function usePollForOrderFulfillment(
  orderId?: number | null,
  options?: Partial<Parameters<typeof useOrderQuery>[0]>
) {
  const orderQuery = useOrderQuery({
    skip: !orderId,
    pollInterval: 3000,
    variables: {
      id: orderId!
    },
    ...options
  })

  const orderStatus = orderQuery.data?.order?.order_status
  const orderFulfilled = orderStatus === OrderStatus.Fulfilled

  // Stop polling when the order reaches a final status.
  useEffect(() => {
    switch (orderStatus) {
      case OrderStatus.Fulfilled:
      case OrderStatus.FulfillmentFailed:
      case OrderStatus.PaymentFailed:
      case OrderStatus.Voided:
        orderQuery.stopPolling()
        break
    }
  }, [orderStatus])

  return [orderFulfilled, orderQuery] as const
}

const failedOrderStatuses = [
  OrderStatus.FulfillmentFailed,
  OrderStatus.PaymentFailed,
  OrderStatus.Voided
]

// Resolves once the order is fulfilled. Rejects when the order reaches a
// failed status or is still unfulfilled after `timeout`.
export async function pollOrderUntilSettled(
  client: ApolloClient<object>,
  orderId: number,
  { interval = 1000, timeout = 60_000 } = {}
): Promise<OrderFragment> {
  const deadline = Date.now() + timeout

  while (true) {
    const { data } = await client.query<OrderQuery, OrderQueryVariables>({
      query: OrderDocument,
      variables: { id: orderId },
      fetchPolicy: "network-only"
    })
    const order = data?.order

    if (order?.order_status === OrderStatus.Fulfilled) return order
    if (order && failedOrderStatuses.includes(order.order_status)) {
      throw new Error(`Order ${orderId} ${order.order_status}.`)
    }
    if (Date.now() >= deadline) {
      throw new Error(`Order ${orderId} was not fulfilled in time.`)
    }

    await new Promise((resolve) => setTimeout(resolve, interval))
  }
}

export default function useOrderPollingQuery(orderId: number) {
  const { me } = usePathwrightContext()

  const [orderFulfilled, orderQuery] = usePollForOrderFulfillment(orderId)

  useEffect(() => {
    if (orderFulfilled) {
      const order = orderQuery.data?.order
      dispatchOrderEvent(ORDER_FULFILLED_EVENT, { user: me, order })

      if (order?.order_type === OrderType.PlatformSubscription) {
        const activationContext = popSignupActivationContext(order.id)
        const spaceId = order.school_id || activationContext?.spaceId

        if (activationContext && spaceId) {
          trackSignupActivateEvents({
            spaceId,
            createdDate:
              activationContext.createdDate || new Date().toISOString(),
            packageKey: activationContext.packageKey,
            type: activationContext.type,
            userId:
              typeof me?.id === "number" ? me.id : activationContext.userId
          })
        }
      }
    }
  }, [orderFulfilled, me?.id])

  return orderQuery
}
